/*
    this file is part of the ExcelJs package but unfortunately is not exposed.
    Source: https://github.com/exceljs/exceljs/blob/master/lib/utils/col-cache.js
 */

const addressRegex = /^[A-Z]+\d+$/;
// =========================================================================
// Column Letter to Number conversion
export const colCache = {
    _dictionary: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z"
    ],
    _l2nFill: 0,
    _l2n: {} as Record<string, number>,
    _n2l: [] as string[],
    _level(n: number) {
        if (n <= 26) {
            return 1;
        }
        if (n <= 26 * 26) {
            return 2;
        }
        return 3;
    },
    _fill(level: number) {
        let c: string;
        let v: number;
        let l1: number;
        let l2: number;
        let l3: number;
        let n = 1;
        if (level >= 4) {
            throw new Error("Out of bounds. Excel supports columns from 1 to 16384");
        }
        if (this._l2nFill < 1 && level >= 1) {
            while (n <= 26) {
                const char = this._dictionary[n - 1];
                if (char === undefined) {
                    throw new Error("Dictionary index out of bounds");
                }
                c = char;
                this._n2l[n] = c;
                this._l2n[c] = n;
                n++;
            }
            this._l2nFill = 1;
        }
        if (this._l2nFill < 2 && level >= 2) {
            n = 27;
            while (n <= 26 + 26 * 26) {
                v = n - (26 + 1);
                l1 = v % 26;
                l2 = Math.floor(v / 26);
                const char2 = this._dictionary[l2];
                const char1 = this._dictionary[l1];
                if (char2 === undefined || char1 === undefined) {
                    throw new Error("Dictionary index out of bounds");
                }
                c = char2 + char1;
                this._n2l[n] = c;
                this._l2n[c] = n;
                n++;
            }
            this._l2nFill = 2;
        }
        if (this._l2nFill < 3 && level >= 3) {
            n = 26 + 26 * 26 + 1;
            while (n <= 16384) {
                v = n - (26 * 26 + 26 + 1);
                l1 = v % 26;
                l2 = Math.floor(v / 26) % 26;
                l3 = Math.floor(v / (26 * 26));
                const char3 = this._dictionary[l3];
                const char2 = this._dictionary[l2];
                const char1 = this._dictionary[l1];
                if (char3 === undefined || char2 === undefined || char1 === undefined) {
                    throw new Error("Dictionary index out of bounds");
                }
                c = char3 + char2 + char1;
                this._n2l[n] = c;
                this._l2n[c] = n;
                n++;
            }
            this._l2nFill = 3;
        }
    },
    l2n(l: string) {
        if (!this._l2n[l]) {
            this._fill(l.length);
        }
        if (!this._l2n[l]) {
            throw new Error(`Out of bounds. Invalid column letter: ${l}`);
        }
        return this._l2n[l];
    },
    n2l(n: number) {
        if (n < 1 || n > 16384) {
            throw new Error(`${n} is out of bounds. Excel supports columns from 1 to 16384`);
        }
        if (!this._n2l[n]) {
            this._fill(this._level(n));
        }
        return this._n2l[n];
    },

    // =========================================================================
    // Address processing
    _hash: {} as Record<
        string,
        { address: string; col: number | undefined; row: number | undefined; $col$row: string }
    >,

    // check if value looks like an address
    validateAddress(value: string) {
        if (!addressRegex.test(value)) {
            throw new Error(`Invalid Address: ${value}`);
        }
        return true;
    },

    // eslint-disable-next-line complexity -- no need to mess with this
    decodeAddress(value: string) {
        const addr = value.length < 5 && this._hash[value];
        if (addr) {
            return addr;
        }
        let hasCol = false;
        let col = "";
        let colNumber: number | undefined = 0;
        let hasRow = false;
        let row = "";
        let rowNumber: number | undefined = 0;

        for (let i = 0, char; i < value.length; i++) {
            char = value.charCodeAt(i);
            // col should before row
            if (!hasRow && char >= 65 && char <= 90) {
                // 65 = 'A'.charCodeAt(0)
                // 90 = 'Z'.charCodeAt(0)
                hasCol = true;
                const valueChar = value[i];
                if (valueChar === undefined) {
                    throw new Error("Invalid column letter");
                }
                col += valueChar;
                // colNumber starts from 1
                colNumber = colNumber * 26 + char - 64;
            } else if (char >= 48 && char <= 57) {
                // 48 = '0'.charCodeAt(0)
                // 57 = '9'.charCodeAt(0)
                hasRow = true;
                const valueChar = value[i];
                if (valueChar === undefined) {
                    throw new Error("Invalid row number");
                }
                row += valueChar;
                // rowNumber starts from 0
                rowNumber = rowNumber * 10 + char - 48;
            } else if (hasRow && hasCol && char !== 36) {
                // 36 = '$'.charCodeAt(0)
                break;
            }
        }

        if (!hasCol) {
            colNumber = undefined;
        } else if (colNumber > 16384) {
            throw new Error(`Out of bounds. Invalid column letter: ${col}`);
        }

        if (!hasRow) {
            rowNumber = undefined;
        }

        // in case $row$col
        value = col + row;

        const address = {
            address: value,
            col: colNumber,
            row: rowNumber,
            $col$row: `$${col}$${row}`
        };

        // mem fix - cache only the tl 100x100 square
        if (colNumber && colNumber <= 100 && rowNumber && rowNumber <= 100) {
            this._hash[value] = address;
            this._hash[address.$col$row] = address;
        }

        return address;
    },

    // convert r,c into structure (if only 1 arg, assume r is address string)
    getAddress(r: string, c: number | undefined) {
        if (c) {
            const col = this.n2l(c);
            if (col === undefined) {
                throw new Error("Invalid column number");
            }
            const address = col + r;
            return this.decodeAddress(address);
        }
        return this.decodeAddress(r);
    },

    // convert [address], [tl:br] into address structures
    decode(value: string) {
        const parts = value.split(":");
        if (parts.length === 2) {
            const part0 = parts[0];
            const part1 = parts[1];
            if (part0 === undefined || part1 === undefined) {
                throw new Error("Invalid range");
            }
            const tl = this.decodeAddress(part0);
            const br = this.decodeAddress(part1);

            if (tl.col === undefined || tl.row === undefined || br.col === undefined || br.row === undefined) {
                throw new Error("Invalid range");
            }

            const result: {
                top: number;
                left: number;
                bottom: number;
                right: number;
                tl?: string;
                br?: string;
                dimensions?: string;
            } = {
                top: Math.min(tl.row, br.row),
                left: Math.min(tl.col, br.col),
                bottom: Math.max(tl.row, br.row),
                right: Math.max(tl.col, br.col)
            };

            const resLeft = this.n2l(result.left);
            const resRight = this.n2l(result.right);

            if (resLeft === undefined || resRight === undefined) {
                throw new Error("Invalid range");
            }

            // reconstruct tl, br and dimensions
            result.tl = resLeft + result.top.toString();
            result.br = resRight + result.bottom.toString();
            result.dimensions = `${result.tl}:${result.br}`;
            return result;
        }
        return this.decodeAddress(value);
    },

    // convert [sheetName!][$]col[$]row[[$]col[$]row] into address or range structures
    decodeEx(value: string) {
        const groups = /(?:(?:(?:'((?:[^']|'')*)')|([^'^ !]*))!)?(.*)/.exec(value);

        if (!groups) {
            throw new Error("Invalid range");
        }

        const sheetName = groups[1] ?? groups[2]; // Quoted and unquoted groups
        const reference = groups[3]; // Remaining address

        if (!reference) {
            throw new Error("Invalid range");
        }

        const parts = reference.split(":");

        if (parts.length > 1) {
            if (!parts[0] || !parts[1]) {
                throw new Error("Invalid range");
            }

            const tl = this.decodeAddress(parts[0]);
            const br = this.decodeAddress(parts[1]);

            if (tl.col === undefined || tl.row === undefined || br.col === undefined || br.row === undefined) {
                throw new Error("Invalid range");
            }

            const top = Math.min(tl.row, br.row);
            const left = Math.min(tl.col, br.col);
            const bottom = Math.max(tl.row, br.row);
            const right = Math.max(tl.col, br.col);

            const resLeft = this.n2l(left);
            const resRight = this.n2l(right);

            if (resLeft === undefined || resRight === undefined) {
                throw new Error("Invalid range");
            }

            const tlValue = resLeft + top.toString();
            const brValue = resRight + bottom.toString();

            return {
                top,
                left,
                bottom,
                right,
                sheetName,
                tl: { address: tl, col: left, row: top, $col$row: `$${this.n2l(left)}$${top}`, sheetName },
                br: {
                    address: br,
                    col: right,
                    row: bottom,
                    $col$row: `$${this.n2l(right)}$${bottom}`,
                    sheetName
                },
                dimensions: `${tlValue}:${brValue}`
            };
        }
        if (reference.startsWith("#")) {
            return sheetName ? { sheetName, error: reference } : { error: reference };
        }

        const address = this.decodeAddress(reference);
        return sheetName ? { sheetName, ...address } : address;
    },

    // convert row,col into address string
    encodeAddress(row: number, col: number) {
        return colCache.n2l(col) + row.toString();
    },

    // convert row,col into string address or t,l,b,r into range
    encode(...args: number[]) {
        switch (args.length) {
            case 2: {
                if (args[0] === undefined || args[1] === undefined) {
                    throw new Error("Invalid row or column");
                }

                return colCache.encodeAddress(args[0], args[1]);
            }
            case 4: {
                if (args[0] === undefined || args[1] === undefined || args[2] === undefined || args[3] === undefined) {
                    throw new Error("Invalid row or column");
                }

                return `${colCache.encodeAddress(args[0], args[1])}:${colCache.encodeAddress(args[2], args[3])}`;
            }
            default:
                throw new Error("Can only encode with 2 or 4 arguments");
        }
    },

    // return true if address is contained within range
    inRange(range: [number, number, number, number, number], address: [number, number]) {
        const [left, top, , right, bottom] = range;
        const [col, row] = address;
        return col >= left && col <= right && row >= top && row <= bottom;
    }
};

export default colCache;
